import { useEffect, type CSSProperties, type HTMLAttributes } from "react";
import { BUTTON_RESET, pressClass } from "../../ui/v2/press";
import { registerTabBarMounted } from "@/lib/platform/tabBarVisibleStore";

export type TabBarTabId = "today" | "friends" | "you";

export interface TabBarTab {
  id: TabBarTabId;
  /** The tab's copy — `t('…')`, never a literal. */
  label: string;
}

export interface TabBarProps extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
  tabs: ReadonlyArray<TabBarTab>;
  active: TabBarTabId;
  onChange: (id: TabBarTabId) => void;
}

/* Icons are inline SVG, stroke 1.5, `currentColor`, on a 20 box — the only
 * icon set v2 has. Sun (Today), two overlapping circles (Friends), a figure
 * (You). Copied path-for-path from the handoff's `ICONS`. */
const ICONS: Record<TabBarTabId, (size: number) => JSX.Element> = {
  today: (size) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <circle cx={10} cy={10} r={4} />
      <path d="M10 1.5v2M10 16.5v2M1.5 10h2M16.5 10h2M4 4l1.4 1.4M14.6 14.6 16 16M16 4l-1.4 1.4M5.4 14.6 4 16" />
    </svg>
  ),
  friends: (size) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <circle cx={7} cy={10} r={4.5} />
      <circle cx={13} cy={10} r={4.5} />
    </svg>
  ),
  you: (size) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <circle cx={10} cy={6.5} r={3.5} />
      <path d="M3.5 17c1-3 3.5-4.5 6.5-4.5s5.5 1.5 6.5 4.5" />
    </svg>
  ),
};

/* DUE-358: Liquid Glass (iOS 26) — a capsule floating over the content, not a
 * panel docked to the edge (DUE-352 docked it; arch read that as "not Liquid
 * Glass any more"). Measured against a native iOS 26 `TabView` on the 26.5
 * simulator: the capsule hugs its tabs and sits centred (`width: fit-content`
 * between `left/right: 0` with auto margins — no `translateX`, `transform` is
 * DUE-303's), 62 tall, lifted `--d-tabbar-lift` off the bottom edge: on a
 * home-indicator iPhone that is the safe area minus 12 (≈ 22pt, just above the
 * indicator, as native), elsewhere a flat 12. Content
 * scrolls on under the capsule and shows through the glass. Its top edge is
 * `--d-tabbar-h + --d-tabbar-lift` above the bottom — `Toast` and `Screen`'s
 * spacer measure from that line.
 *
 * `position: fixed` resolves against the viewport on a phone and against the
 * 480px column on a wide browser (`WebStage`'s `contain: paint`), so the
 * capsule centres in the column, never in the whole browser window. */
const BAR: CSSProperties = {
  position: "fixed",
  left: 0,
  right: 0,
  bottom: "var(--d-tabbar-lift)",
  width: "fit-content",
  maxWidth: "calc(100% - 2 * var(--space-4))",
  marginLeft: "auto",
  marginRight: "auto",
  /* 05-SHARED.md: the bar is 20, the scroll container is 1, the grain is 30 —
   * "таб-бар под зерном, это правильно". Without this the bar is z-index auto
   * and the scroller (z-index 1) covers it: the bar renders and every tap dies
   * in the scroll surface. */
  zIndex: 20,
  boxSizing: "border-box",
  height: "var(--d-tabbar-h)",
  borderRadius: "var(--r-pill)",
  /* The lens inside is 54 tall: 4 of glass around it on every side. */
  padding: "var(--space-1)",
  /* The glass is its own underlay (GLASS below); this box only lays the tabs
   * out on top of it, so the labels and icons never sit in a layer that
   * carries a backdrop filter or a compositing hint. */
  isolation: "isolate",
  display: "flex",
  alignItems: "stretch",
};

/* The glass itself — an underlay filling the capsule behind the tabs.
 * DUE-358 follow-up, measured against a native iOS 26 `TabView` at 3x: the
 * first pass (graphite `--d-raise` wash, blur 24 + saturate 1.8, a 10% rim, a
 * 32px lift) turned the warm ground into a flat brown slab with a soft halo —
 * arch: «нечётко, всё поплыло». Native glass is clear rather than milky: a
 * thin wash of the ground itself (neutral near-black at sunset, paper at
 * dawn), a lighter blur, a crisp bright rim and a barely-there lift.
 * White and black are materials here, the same on both grounds. */
const GLASS: CSSProperties = {
  position: "absolute",
  inset: 0,
  zIndex: -1,
  pointerEvents: "none",
  borderRadius: "var(--r-pill)",
  /* A thin wash of the ground itself (neutral near-black at sunset, paper at
   * dawn) lifted by an even 8% of white, so the glass sits a step lighter
   * than what it floats over, as native glass does on both grounds. */
  background:
    "linear-gradient(rgba(255,255,255,.08), rgba(255,255,255,.08)), color-mix(in srgb, var(--d-ground) 45%, transparent)",
  backdropFilter: "blur(12px) saturate(1.4)",
  WebkitBackdropFilter: "blur(12px) saturate(1.4)",
  /* A crisp rim all round — `--d-line-2`, light at sunset and dark at dawn,
   * so the edge holds on both grounds (a white rim vanished on dawn) — and a
   * specular highlight along the top where the light falls. The soft lift
   * under the capsule is the one sanctioned outer shadow (00-DESIGN-SYSTEM
   * exception, arch 2026-09-24). */
  border: "1px solid var(--d-line-2)",
  boxShadow: "0 2px 12px rgba(0,0,0,.12), inset 0 1px 0 rgba(255,255,255,.22)",
  /* DUE-303: on iPhone (WKWebView, Telegram) the blur flickered while text
   * scrolled under the bar — WebKit re-rasterised the backdrop in jumps. The
   * underlay is pinned to its own compositing layer that the scroller's
   * repaints and the grain's `overlay` blend above (z 30) do not invalidate.
   * Only the underlay: the tabs on top stay out of it and rasterise sharp. */
  transform: "translateZ(0)",
  willChange: "transform",
  backfaceVisibility: "hidden",
  WebkitBackfaceVisibility: "hidden",
};

/* Each tab is the full 54 of the capsule's inner height and at least 88 wide
 * (native iOS 26 measures ≈ 88 per tab) — the touch target clears 44pt
 * whether or not it is the active one. A longer label widens its own tab. */
const TAB: CSSProperties = {
  ...BUTTON_RESET,
  color: "var(--d-fg)",
  flex: "none",
  minWidth: "calc(var(--space-20) + var(--space-2))",
  display: "flex",
  alignItems: "stretch",
  justifyContent: "stretch",
};

const CELL: CSSProperties = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 2,
  padding: "0 var(--space-3)",
  borderRadius: "var(--r-pill)",
  /* Idle cells draw the same 1px edge in transparent, so choosing a tab moves
   * nothing by a pixel. */
  border: "1px solid transparent",
};

/* The active tab is the lens — a second capsule inside the bar: a flat
 * `--d-fg` tint (light glass at sunset, dark at dawn) with a `--d-line` rim.
 * No sheen gradient — that is what made the first pass milky. Not `--d-fill`:
 * an opaque slab down here would be the loudest thing on the screen. */
const LENS: CSSProperties = {
  ...CELL,
  background: "color-mix(in srgb, var(--d-fg) 14%, transparent)",
  border: "1px solid var(--d-line)",
};

/* The prototype tightened the label inside the pill to 10/12; arch read that
 * as the word shrinking the moment a tab is chosen (DUE-282) — the active tab
 * keeps `--t-tab`, the same size as its idle neighbours. */
const LABEL: CSSProperties = {
  font: "var(--t-tab)",
  letterSpacing: ".01em",
};

/**
 * The app's tab bar in v2 — a floating Liquid Glass capsule (DUE-358) with
 * three tabs; the active one is a lens of glass inside it.
 *
 * Geometry and surface: 05-SHARED.md §«Таб-бар» and
 * `docs/design/handoff/design/components/today/TabBar.jsx`.
 */
export function TabBar({
  tabs,
  active,
  onChange,
  className,
  style,
  ...rest
}: TabBarProps): JSX.Element {
  // Task 7b (DUE-318): reports this bar's presence to `<Toast/>` so it can
  // sit above it — the one place that already knows "the tab bar is shown".
  useEffect(() => registerTabBarMounted(), []);

  return (
    <div role="tablist" className={className} style={{ ...BAR, ...style }} {...rest}>
      <span aria-hidden="true" data-tab-glass style={GLASS} />
      {tabs.map((tab) => {
        const selected = tab.id === active;
        const icon = ICONS[tab.id];
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={selected}
            className={pressClass()}
            onClick={() => onChange(tab.id)}
            /* Every label in full `--d-fg`, as native: the lens marks the
             * active tab. `--d-fg-2` on glass read as blurred text. */
            style={TAB}
          >
            <span data-tab-pill={selected ? "" : undefined} style={selected ? LENS : CELL}>
              {icon(18)}
              <span style={LABEL}>{tab.label}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default TabBar;
